// SAM-568 — async booking-engine sync. `POST /bookings/services/{id}/engine-sync` can take
// longer than API Gateway's 29s limit; the server finishes but the admin used to see a 504 and
// report a failure that never happened. The backend contract (rolling out in parallel):
//
//   POST -> 202 {"success": true, "data": {"sync": {"state": "running", "started_at": "<iso>"}}}
//   GET  -> today's payload plus data.sync = {state, started_at, finished_at, error}
//
// Until the new backend ships, POST still answers synchronously with 200 (today's payload) or,
// on a long sync, a 504 / network timeout. This module is the pure decision/polling core so the
// whole state machine is unit-testable without React or a network — see
// `useSyncServiceToEngine` in `src/hooks/useServices.ts` for the thin adapter that wires it to
// axios + TanStack Query, and `engineSyncPolling.test.ts` for the behaviour this covers.

export type EngineSyncState = 'idle' | 'running' | 'succeeded' | 'failed';

export interface EngineSyncInfo {
  state: EngineSyncState;
  started_at?: string;
  finished_at?: string;
  error?: string | null;
}

// The GET payload shape, loosened to `unknown` fields since callers only care about `sync` here.
export interface EngineSyncStatusPayload {
  sync?: EngineSyncInfo | null;
  [key: string]: unknown;
}

export interface EngineSyncPostResponse {
  httpStatus: number;
  data?: EngineSyncStatusPayload;
}

export type EngineSyncOutcome =
  | { kind: 'succeeded'; data?: EngineSyncStatusPayload }
  | { kind: 'failed'; message: string }
  | { kind: 'neutral'; message: string }
  | { kind: 'timeout'; message: string }
  | { kind: 'cancelled' };

export const NEUTRAL_SYNC_MESSAGE =
  'Sync is taking longer than usual — check again in a minute.';
export const TIMEOUT_SYNC_MESSAGE =
  'Booking engine sync timed out — check again in a minute.';
export const DEFAULT_SYNC_ERROR_MESSAGE = 'Booking engine sync failed';

export const DEFAULT_POLL_INTERVAL_MS = 3000;
export const DEFAULT_MAX_WAIT_MS = 5 * 60 * 1000; // 5 minutes — 202 (new backend) polling.
export const LEGACY_MAX_WAIT_MS = 90 * 1000; // 90 seconds — 504-triggered legacy polling.

type Sleep = (ms: number) => Promise<void>;
type Now = () => number;

const defaultSleep: Sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function safeFetchStatus(
  fetchStatus: () => Promise<EngineSyncStatusPayload>
): Promise<EngineSyncStatusPayload> {
  try {
    return await fetchStatus();
  } catch {
    // A transient GET failure mid-poll is not itself a sync failure — keep polling until the
    // deadline instead of surfacing it.
    return {};
  }
}

export interface PollOptions {
  fetchStatus: () => Promise<EngineSyncStatusPayload>;
  intervalMs?: number;
  maxWaitMs?: number;
  isCancelled?: () => boolean;
  sleep?: Sleep;
  now?: Now;
  /** Reuse an already-fetched payload for the first iteration instead of an extra GET. */
  initialPayload?: EngineSyncStatusPayload;
}

/**
 * Polls GET every `intervalMs` until `sync.state` is `succeeded`/`failed`, `isCancelled()` flips
 * true (caller must stop touching state — e.g. the owning component unmounted), or `maxWaitMs`
 * elapses.
 */
export async function pollUntilSyncFinishes(options: PollOptions): Promise<EngineSyncOutcome> {
  const {
    fetchStatus,
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxWaitMs = DEFAULT_MAX_WAIT_MS,
    isCancelled = () => false,
    sleep = defaultSleep,
    now = Date.now,
    initialPayload,
  } = options;

  const deadline = now() + maxWaitMs;
  let payload: EngineSyncStatusPayload | undefined = initialPayload;

  while (true) {
    if (isCancelled()) return { kind: 'cancelled' };

    if (!payload) {
      payload = await safeFetchStatus(fetchStatus);
    }

    if (isCancelled()) return { kind: 'cancelled' };

    const state = payload.sync?.state;
    if (state === 'succeeded') return { kind: 'succeeded', data: payload };
    if (state === 'failed') {
      return { kind: 'failed', message: payload.sync?.error || DEFAULT_SYNC_ERROR_MESSAGE };
    }

    payload = undefined; // force a fresh GET next loop

    if (now() >= deadline) return { kind: 'timeout', message: TIMEOUT_SYNC_MESSAGE };

    await sleep(intervalMs);
  }
}

export interface LegacyPollOptions extends PollOptions {
  /** Returns true once today's (pre-`sync`-field) GET payload shows the sync as finished. */
  isFinished: (payload: EngineSyncStatusPayload) => boolean;
}

/**
 * Old-backend fallback: no `sync` field to read, so poll up to `maxWaitMs` (default 90s) for a
 * caller-supplied signal that the mapping now reflects a finished sync.
 */
export async function pollLegacyUntilSignal(options: LegacyPollOptions): Promise<EngineSyncOutcome> {
  const {
    fetchStatus,
    isFinished,
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxWaitMs = LEGACY_MAX_WAIT_MS,
    isCancelled = () => false,
    sleep = defaultSleep,
    now = Date.now,
    initialPayload,
  } = options;

  const deadline = now() + maxWaitMs;
  let payload: EngineSyncStatusPayload | undefined = initialPayload;

  while (true) {
    if (isCancelled()) return { kind: 'cancelled' };

    if (!payload) {
      payload = await safeFetchStatus(fetchStatus);
    }

    if (isCancelled()) return { kind: 'cancelled' };

    if (isFinished(payload)) return { kind: 'succeeded', data: payload };

    payload = undefined;

    if (now() >= deadline) return { kind: 'neutral', message: NEUTRAL_SYNC_MESSAGE };

    await sleep(intervalMs);
  }
}

export interface RunEngineSyncOptions {
  post: () => Promise<EngineSyncPostResponse>;
  fetchStatus: () => Promise<EngineSyncStatusPayload>;
  intervalMs?: number;
  /** Max wait for 202 (new backend) / sync-field polling. Default 5 minutes. */
  maxWaitMs?: number;
  /** Max wait for the old-backend, no-`sync`-field fallback. Default 90s. */
  legacyMaxWaitMs?: number;
  isCancelled?: () => boolean;
  sleep?: Sleep;
  now?: Now;
  /**
   * Old-backend-only signal: given today's GET payload (no `sync` field), returns whether the
   * mapping now reflects a finished sync. When omitted there is no reliable signal to poll for,
   * so a 504/timeout against the old backend resolves straight to the neutral "taking longer"
   * state rather than guessing.
   */
  isLegacyFinished?: (payload: EngineSyncStatusPayload) => boolean;
  /** Extracts a human message from a non-504 POST failure. */
  extractErrorMessage?: (error: unknown, response?: EngineSyncPostResponse) => string;
}

const defaultExtractErrorMessage = (error: unknown): string => {
  const anyError = error as any;
  return anyError?.response?.data?.message || anyError?.message || DEFAULT_SYNC_ERROR_MESSAGE;
};

/**
 * Orchestrates one full sync attempt: POST, then react to its outcome per the contract in the
 * module docstring. Never throws — every path resolves to an `EngineSyncOutcome`, so callers
 * (the mutation hook) decide what counts as a UI-facing error.
 */
export async function runEngineSync(options: RunEngineSyncOptions): Promise<EngineSyncOutcome> {
  const {
    post,
    fetchStatus,
    intervalMs,
    maxWaitMs,
    legacyMaxWaitMs,
    isCancelled = () => false,
    sleep,
    now,
    isLegacyFinished,
    extractErrorMessage = defaultExtractErrorMessage,
  } = options;

  const pollBase = { fetchStatus, intervalMs, isCancelled, sleep, now };

  let response: EngineSyncPostResponse | undefined;
  let postError: unknown;
  try {
    response = await post();
  } catch (error) {
    postError = error;
  }

  if (isCancelled()) return { kind: 'cancelled' };

  const status = response?.httpStatus;
  const isGatewayTimeout = postError !== undefined || status === 504;

  if (!isGatewayTimeout && status === 200) {
    // Legacy synchronous backend — behave exactly as today.
    return { kind: 'succeeded', data: response?.data };
  }

  if (!isGatewayTimeout && status === 202) {
    return pollUntilSyncFinishes({
      ...pollBase,
      maxWaitMs,
      initialPayload: response?.data,
    });
  }

  if (isGatewayTimeout) {
    // Never report failure for a Gateway timeout / network error on the POST — the server may
    // still be working. Switch to polling GET instead.
    const probe = await safeFetchStatus(fetchStatus);
    if (isCancelled()) return { kind: 'cancelled' };

    if (probe.sync) {
      return pollUntilSyncFinishes({ ...pollBase, maxWaitMs, initialPayload: probe });
    }

    if (isLegacyFinished) {
      if (isLegacyFinished(probe)) return { kind: 'succeeded', data: probe };
      return pollLegacyUntilSignal({
        ...pollBase,
        maxWaitMs: legacyMaxWaitMs,
        isFinished: isLegacyFinished,
      });
    }

    return { kind: 'neutral', message: NEUTRAL_SYNC_MESSAGE };
  }

  // Any other status (a real 4xx/5xx from the sync itself) — unchanged failure behaviour.
  return { kind: 'failed', message: extractErrorMessage(postError, response) };
}
